import { levelAllows, looksLikeHtml, plainTextToHtml, errorWords } from "@alm/domain";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { PolicyAttachments } from "@/components/app/policies/policy-attachments";
import { PolicyContent } from "@/components/app/policies/policy-content";
import { PolicyEditor } from "@/components/app/policies/editor/policy-editor";
import { RelatedPolicies } from "@/components/app/policies/related-policies";
import { VersionTiles } from "@/components/app/policies/version-tiles";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft } from "@/lib/icons";
import { orpc } from "@/lib/orpc";
import { invalidateKeys } from "@/lib/query/invalidate-keys";
import { useCan } from "@/lib/policy";
import { PolicyAdmin } from "../-components/policy-admin";

export const Route = createFileRoute("/_authenticated/_knowledge/policies/$policyId/")({
  component: PolicyDetail,
});

const fail = (error: Error) => toast.error(errorWords(error));

/** The wording as the editor opens it: plain rows from before the editor become paragraphs. */
const editable = (content: string) => (looksLikeHtml(content) ? content : plainTextToHtml(content));

/** One policy: the wording in force, its files and relations, and every version as a tile. */
function PolicyDetail() {
  const { policyId } = Route.useParams();
  const canManage = useCan("policy:manage");
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const policy = useQuery(orpc.knowledge.policies.get.queryOptions({ input: { policyId } }));

  const refresh = async () => {
    await invalidateKeys(queryClient, [orpc.knowledge.policies.key()]);
  };

  const saveDraft = useMutation(
    orpc.knowledge.policies.updateVersion.mutationOptions({
      onSuccess: async () => {
        toast.success("Draft saved");
        setEditing(false);
        await refresh();
      },
      onError: fail,
    }),
  );
  const publish = useMutation(
    orpc.knowledge.policies.publishVersion.mutationOptions({
      onSuccess: async () => {
        toast.success("Version published");
        setEditing(false);
        await refresh();
      },
      onError: fail,
    }),
  );
  const newVersion = useMutation(
    orpc.knowledge.policies.createVersion.mutationOptions({
      onSuccess: async () => {
        toast.success("New draft started");
        await refresh();
      },
      onError: fail,
    }),
  );
  const acknowledge = useMutation(
    orpc.knowledge.policies.acknowledge.mutationOptions({
      onSuccess: async () => {
        toast.success("Reading recorded");
        await refresh();
      },
      onError: fail,
    }),
  );
  const revert = useMutation(
    orpc.knowledge.policies.revertVersion.mutationOptions({
      onSuccess: async (version) => {
        toast.success(`Draft v${version.versionNumber} opened from the old wording`);
        await refresh();
      },
      onError: fail,
    }),
  );
  const duplicate = useMutation(
    orpc.knowledge.policies.duplicate.mutationOptions({
      onSuccess: async (copy) => {
        toast.success("Policy copied");
        await refresh();
        // Straight to the copy: the reason for duplicating is to start editing
        // it, and leaving the reader on the original hides what just happened.
        await navigate({ to: "/policies/$policyId", params: { policyId: copy.id } });
      },
      onError: fail,
    }),
  );

  if (policy.isPending) return <Skeleton className="h-64 w-full" />;
  if (policy.isError) {
    return (
      <EmptyState
        title="This policy could not be opened"
        description="It may have been removed, or it belongs to a building you cannot see."
        action={
          <Button asChild variant="outline">
            <Link to="/policies">All policies</Link>
          </Button>
        }
      />
    );
  }

  const detail = policy.data;
  const published = detail.versions.find((version) => version.id === detail.publishedVersionId);
  const openDraft = detail.versions.find((version) => version.status === "draft");
  // Editing the wording, the files and the relations is the edit level; the
  // manage permission implies it. Publishing stays with the managers.
  const canEdit = canManage || levelAllows(detail.myLevel, "edit");
  const busy = publish.isPending || revert.isPending || newVersion.isPending || duplicate.isPending;

  const startEditing = () => {
    if (!openDraft) return;
    setDraft(editable(openDraft.content ?? ""));
    setEditing(true);
  };

  return (
    <>
      <div className="print:hidden">
        <Button variant="ghost" size="sm" asChild className="-ml-2">
          <Link to="/policies">
            <ArrowLeft className="size-4" />
            All policies and procedures
          </Link>
        </Button>
      </div>

      <PageHeader
        title={detail.title}
        meta={detail.category ?? "Policy"}
        actions={
          <div className="flex flex-wrap gap-2">
            {/* What a reader is shown when asked to sign for it. Who is asked at
                all waits in the overflow menu with the other occasional jobs. */}
            <Button variant="outline" asChild>
              <Link to="/policies/$policyId/view" params={{ policyId }}>
                Read-only view
              </Link>
            </Button>
            {canEdit ? (
              openDraft ? (
                <>
                  {!editing ? (
                    <Button variant="outline" onClick={startEditing}>
                      Edit the draft
                    </Button>
                  ) : null}
                  {canManage ? (
                    <Button
                      disabled={busy}
                      onClick={() => publish.mutate({ versionId: openDraft.id })}
                    >
                      Publish v{openDraft.versionNumber}
                    </Button>
                  ) : null}
                </>
              ) : (
                <Button disabled={busy} onClick={() => newVersion.mutate({ policyId })}>
                  Start a new version
                </Button>
              )
            ) : null}
            {/* Access, the read report, renaming and duplicating: at most one
                primary and two secondaries stay in the row, the rest go here. */}
            {canManage ? (
              <PolicyAdmin
                policy={detail}
                publishedVersionId={published?.id ?? null}
                duplicating={duplicate.isPending}
                onDuplicate={() => duplicate.mutate({ policyId })}
              />
            ) : null}
          </div>
        }
      />

      {editing && openDraft ? (
        <Card>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-medium">Draft v{openDraft.versionNumber}</p>
              <p className="text-muted-foreground text-xs">
                Headings, lists, tables, pictures, videos, links and cross-references to other
                policies.
              </p>
            </div>
            <PolicyEditor policyId={policyId} value={draft} onChange={setDraft} />
            <div className="flex gap-2">
              <Button
                disabled={saveDraft.isPending}
                onClick={() => saveDraft.mutate({ versionId: openDraft.id, content: draft })}
              >
                Save draft
              </Button>
              <Button variant="ghost" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {published ? (
        <Card>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-medium">In force</h2>
              <Badge variant="secondary">v{published.versionNumber}</Badge>
              {published.effectiveDate ? (
                <span className="text-muted-foreground text-sm">
                  from {String(published.effectiveDate).slice(0, 10)}
                </span>
              ) : null}
            </div>
            <PolicyContent content={published.content} className="max-w-3xl" />
            {/* Outline here: this is the editing screen, where the header's action is
                the filled one; the read-only view carries the filled sign-off. Somebody asked
                to sign it off signs there, with a drawn signature. */}
            {detail.signOffRequired ? (
              <Button asChild size="sm" variant="outline">
                <Link to="/policies/$policyId/view" params={{ policyId }}>
                  Read and sign off
                </Link>
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                disabled={acknowledge.isPending}
                onClick={() => acknowledge.mutate({ versionId: published.id })}
              >
                I have read this
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <EmptyState
          title="Nothing published yet"
          {...(openDraft && !editing
            ? { description: "The draft is below; open it to keep writing." }
            : {})}
        />
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <PolicyAttachments policyId={policyId} canEdit={canEdit} />
        <RelatedPolicies policyId={policyId} canEdit={canEdit} />
      </div>

      <div className="space-y-3">
        <div className="flex items-baseline justify-between gap-2">
          <h2 className="text-sm font-medium">Versions</h2>
          <p className="text-muted-foreground text-xs">
            {detail.versions.length === 1 ? "1 version" : `${detail.versions.length} versions`}
            {", "}open a tile to read it
          </p>
        </div>
        <VersionTiles
          versions={detail.versions}
          canEdit={canEdit}
          canManage={canManage}
          hasOpenDraft={Boolean(openDraft)}
          busy={busy}
          onEditDraft={startEditing}
          onPublish={(version) => publish.mutate({ versionId: version.id })}
          onRevert={(version) => revert.mutate({ versionId: version.id })}
        />
      </div>
    </>
  );
}
